import React, { useCallback, useEffect, useState } from 'react';
import { alpacaService } from '../../services/AlpacaService';
import type {
  ScalperConfig,
  ScalperInstance,
  ScalperPosition,
  ScalperPrice,
  ScalperStats,
  ScalperStatus,
  ScalperTrade,
} from '../../services/AlpacaService';
import { useToast } from '../../context/ToastContext';
import styles from './Scalper.module.css';

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatTimeLocal(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
}

function pnlClass(v: number): string {
  if (v > 0) return styles.pnlPos;
  if (v < 0) return styles.pnlNeg;
  return styles.pnlFlat;
}

function actionClass(action: string): string {
  if (action.startsWith('ENTER')) return styles.actionEnter;
  if (action === 'REVERSE') return styles.actionReverse;
  if (action === 'EXIT') return styles.actionExit;
  if (action === 'TRAIL_TIGHTEN') return styles.actionTrail;
  return styles.actionHold;
}

/**
 * Render minutes-until-expiry for the circuit-breaker timer. Returns 'now'
 * when the expiry has already passed (the next tick will clear it).
 */
function cbMinutesLeft(expiresAt: string | null | undefined): string {
  if (!expiresAt) return '';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return 'now';
  return `${Math.max(1, Math.round(ms / 60000))}m left`;
}

/**
 * Pick the phase floor that matches the current ET time so the strip can
 * show "what would Sonnet have to clear right now to enter". Crypto
 * defaults to CRYPTO_24X7. We only need a coarse mapping.
 */
function currentPhaseKey(assetClass: string): string {
  if (assetClass === 'crypto') return 'CRYPTO_24X7';
  // Convert "now" to ET hh:mm using Intl.
  const fmt = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
    timeZone: 'America/New_York',
  });
  const parts = fmt.formatToParts(new Date());
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  const t = hour * 60 + minute;
  if (t < 9 * 60 + 30) return 'PRE';
  if (t < 10 * 60) return 'OPEN_DRIVE';
  if (t < 11 * 60 + 30) return 'MORNING';
  if (t < 13 * 60 + 30) return 'MIDDAY';
  if (t < 15 * 60) return 'AFTERNOON';
  if (t < 15 * 60 + 55) return 'POWER_HOUR';
  if (t < 16 * 60) return 'CLOSE';
  return 'AFTER';
}

const Scalper: React.FC = () => {
  const { addToast } = useToast();
  const [instances, setInstances] = useState<ScalperInstance[]>([]);
  const [activeInstance, setActiveInstance] = useState<string>('spy');
  const [prices, setPrices] = useState<Record<string, ScalperPrice>>({});
  const [config, setConfig] = useState<ScalperConfig | null>(null);
  const [status, setStatus] = useState<ScalperStatus | null>(null);
  const [stats, setStats] = useState<ScalperStats | null>(null);
  const [trades, setTrades] = useState<ScalperTrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingCreds, setSavingCreds] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');

  // Initial instance list — runs once.
  useEffect(() => {
    let cancelled = false;
    alpacaService
      .getScalperInstances()
      .then((resp) => {
        if (cancelled) return;
        setInstances(resp.instances);
        if (resp.instances.length > 0) {
          setActiveInstance(resp.default || resp.instances[0].key);
        }
      })
      .catch((err) => console.error('Scalper instances load error:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = useCallback(async () => {
    if (!activeInstance) return;
    try {
      const [insts, c, s, st, t] = await Promise.all([
        alpacaService.getScalperInstances(),
        alpacaService.getScalperConfig(activeInstance),
        alpacaService.getScalperStatus(activeInstance),
        alpacaService.getScalperStats(1, activeInstance),
        alpacaService.getScalperTrades(50, activeInstance),
      ]);
      setInstances(insts.instances);
      setConfig(c);
      setStatus(s);
      setStats(st);
      setTrades(t.trades);
    } catch (err: any) {
      console.error('Scalper load error:', err);
    }
  }, [activeInstance]);

  useEffect(() => {
    setLoading(true);
    reload().finally(() => setLoading(false));
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
  }, [reload]);

  // Price ticker — independent 5s poll. Conservative cadence, well under
  // any rate limit but fresh enough to feel real-time when watching a trade.
  useEffect(() => {
    let cancelled = false;
    const fetchPrices = () => {
      alpacaService
        .getScalperPrices()
        .then((resp) => {
          if (!cancelled) setPrices(resp.prices || {});
        })
        .catch((err) => console.error('Scalper prices load error:', err));
    };
    fetchPrices();
    const id = setInterval(fetchPrices, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const handleToggle = async (enabled: boolean) => {
    try {
      await alpacaService.saveScalperConfig({ enabled }, activeInstance);
      setConfig((c) => (c ? { ...c, enabled } : c));
      setInstances((list) => list.map((i) => (i.key === activeInstance ? { ...i, enabled } : i)));
      addToast(enabled ? 'Scalper enabled' : 'Scalper disabled', 'success');
    } catch (err: any) {
      addToast(err.message || 'Failed to update', 'error');
    }
  };

  const handleSaveCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim() || !secretKey.trim()) {
      addToast('Both API key and secret are required', 'error');
      return;
    }
    setSavingCreds(true);
    try {
      await alpacaService.saveScalperCredentials({
        api_key: apiKey.trim(),
        secret_key: secretKey.trim(),
      });
      addToast('Scalper credentials saved', 'success');
      setApiKey('');
      setSecretKey('');
      await reload();
    } catch (err: any) {
      addToast(err.message || 'Failed to save credentials', 'error');
    } finally {
      setSavingCreds(false);
    }
  };

  const handleDeleteCredentials = async () => {
    if (!window.confirm('Remove scalper credentials? The scalper will be disabled.')) return;
    try {
      await alpacaService.deleteScalperCredentials();
      addToast('Credentials removed', 'info');
      await reload();
    } catch (err: any) {
      addToast(err.message || 'Failed to remove credentials', 'error');
    }
  };

  const handleTriggerTick = async () => {
    setTriggerLoading(true);
    try {
      const resp = await alpacaService.triggerScalperTick(activeInstance);
      const r = resp.result || {};
      addToast(`Tick: ${r.status || 'done'}${r.action ? ` (${r.action})` : ''}`, 'info');
      await reload();
    } catch (err: any) {
      addToast(err.message || 'Tick failed', 'error');
    } finally {
      setTriggerLoading(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading scalper...</div>;
  }

  const pos = status?.position;
  const lastDec = status?.lastDecision;
  const symbol = config?.symbol || 'SPY';
  const isCrypto = config?.assetClass === 'crypto';
  const equityPos = pos as ScalperPosition | null | undefined;

  const formatPrice = (p: number, assetClass: 'equity' | 'crypto'): string => {
    if (!p) return '—';
    if (assetClass === 'crypto' && p >= 100) return `$${p.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    if (assetClass === 'crypto')
      return `$${p.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
    return `$${p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className={styles.container}>
      {instances.length > 0 && (
        <div className={styles.priceTicker} aria-label="Live prices">
          {instances.map((inst) => {
            const price = prices[inst.key];
            const change = price?.changePct ?? 0;
            const changeClass = change > 0 ? styles.tickerPos : change < 0 ? styles.tickerNeg : styles.tickerFlat;
            return (
              <button
                key={`ticker-${inst.key}`}
                type="button"
                className={`${styles.tickerCell} ${inst.key === activeInstance ? styles.tickerCellActive : ''}`}
                onClick={() => setActiveInstance(inst.key)}
                title={`Switch to ${inst.label}`}
              >
                <span className={styles.tickerSymbol}>{inst.label}</span>
                <span className={styles.tickerPrice}>{price ? formatPrice(price.price, price.assetClass) : '—'}</span>
                <span className={`${styles.tickerChange} ${changeClass}`}>
                  {price && price.price > 0 ? `${change >= 0 ? '+' : ''}${change.toFixed(2)}%` : ''}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {instances.length > 1 && (
        <div className={styles.instanceTabs} role="tablist">
          {instances.map((inst) => {
            const active = inst.key === activeInstance;
            return (
              <button
                key={inst.key}
                role="tab"
                aria-selected={active}
                className={`${styles.instanceTab} ${active ? styles.instanceTabActive : ''}`}
                onClick={() => setActiveInstance(inst.key)}
              >
                <span className={inst.enabled ? styles.statusDotOn : styles.statusDotOff} aria-hidden />
                <span className={styles.instanceLabel}>{inst.label}</span>
                <span className={styles.instanceClass}>{inst.assetClass === 'crypto' ? '24/7' : 'RTH'}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>AI Scalper</h1>
          <p className={styles.subtitle}>
            Single-symbol intraday trader (currently: <strong>{symbol}</strong>). Claude Sonnet evaluates every 2
            minutes {isCrypto ? '24/7 (crypto)' : 'during US market hours'} and opens / manages one position at a time
            on the dedicated Alpaca paper account.
          </p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.toggle}>
            <span className={config?.enabled ? styles.statusDotOn : styles.statusDotOff} />
            <span>{config?.enabled ? 'Running' : 'Paused'}</span>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => handleToggle(!config?.enabled)}
              disabled={!config?.credentialsConfigured}
              title={config?.credentialsConfigured ? '' : 'Save Alpaca paper credentials first'}
            >
              {config?.enabled ? 'Pause' : 'Enable'}
            </button>
          </div>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleTriggerTick}
            disabled={triggerLoading || !config?.credentialsConfigured}
          >
            {triggerLoading ? 'Running...' : 'Run tick now'}
          </button>
        </div>
      </div>

      {/* ── Active config strip (params + CB state) ── */}
      {status?.activeConfig
        ? (() => {
            const cfg = status.activeConfig!;
            const cb = status.circuitBreaker;
            const phaseKey = currentPhaseKey(status.assetClass);
            const baseFloor = cfg.phaseFloors[phaseKey] ?? cfg.entryConfidenceMin;
            const effectiveFloor = baseFloor + (cb?.armed ? cfg.circuitBreaker.floorBump : 0);
            return (
              <div className={styles.configStrip} role="status" aria-label="Active scalper configuration">
                <div className={styles.configChip}>
                  <span className={styles.configKey}>Stop</span>
                  <span className={styles.configVal}>{cfg.initialStopAtr.toFixed(1)}×ATR</span>
                </div>
                <div className={styles.configChip}>
                  <span className={styles.configKey}>Scale-out</span>
                  <span className={styles.configVal}>+{cfg.scaleOutTpAtr.toFixed(1)}×ATR (50%)</span>
                </div>
                <div className={styles.configChip}>
                  <span className={styles.configKey}>Phase</span>
                  <span className={styles.configVal}>
                    {phaseKey} · floor {effectiveFloor}
                    {cb?.armed ? <span className={styles.cbBumpHint}> (+{cfg.circuitBreaker.floorBump})</span> : null}
                  </span>
                </div>
                <div className={`${styles.configChip} ${cb?.armed ? styles.cbArmed : ''}`}>
                  <span className={styles.configKey}>Circuit breaker</span>
                  <span className={styles.configVal}>
                    {cb?.armed ? `Armed · ${cb.loss_count}L streak · ${cbMinutesLeft(cb.expires_at)}` : 'Idle'}
                  </span>
                </div>
              </div>
            );
          })()
        : null}

      {/* ── Credentials ── */}
      {!config?.credentialsConfigured && (
        <div className={styles.credsBanner}>
          <h3>Connect the scalper's paper account</h3>
          <p>
            The scalper runs against a <em>dedicated</em> Alpaca paper account — separate from your main strategist
            account so the stats stay clean. Paste the API key + secret from your scalper paper dashboard. We store them
            encrypted at rest and never expose them in logs.
          </p>
          <form onSubmit={handleSaveCredentials} className={styles.credsForm}>
            <label>
              <span>API key</span>
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="PK..."
                autoComplete="off"
              />
            </label>
            <label>
              <span>Secret key</span>
              <input
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder="••••••••"
                autoComplete="off"
              />
            </label>
            <button type="submit" className={styles.btnPrimary} disabled={savingCreds}>
              {savingCreds ? 'Saving...' : 'Save credentials'}
            </button>
          </form>
        </div>
      )}

      {/* ── Session stats ── */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Today P&amp;L</div>
          <div className={`${styles.statValue} ${pnlClass(stats?.totalPnl ?? 0)}`}>
            {stats && stats.totalPnl >= 0 ? '+' : ''}${stats?.totalPnl.toFixed(2) ?? '0.00'}
          </div>
          <div className={styles.statHint}>
            {stats?.trades ?? 0} round-trips
            {stats && (stats.lockedPnl ?? 0) > 0 ? (
              <>
                {' • '}
                <span className={styles.pnlPos}>+${stats.lockedPnl?.toFixed(2)} locked</span>
              </>
            ) : null}
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Win rate</div>
          <div className={styles.statValue}>{stats?.winRate.toFixed(0) ?? '0'}%</div>
          <div className={styles.statHint}>
            {stats?.wins ?? 0}W / {stats?.losses ?? 0}L
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Best / Worst</div>
          <div className={styles.statValue}>
            <span className={styles.pnlPos}>+${stats?.bestPnl.toFixed(2) ?? '0.00'}</span>
            {' / '}
            <span className={styles.pnlNeg}>${stats?.worstPnl.toFixed(2) ?? '0.00'}</span>
          </div>
          <div className={styles.statHint}>per trade</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Avg hold</div>
          <div className={styles.statValue}>{stats?.avgHoldMinutes.toFixed(1) ?? '0'}m</div>
          <div className={styles.statHint}>across today's trades</div>
        </div>
      </div>

      {/* ── Edge map (bucketed setup stats Sonnet sees in its prompt) ── */}
      {status?.edgeMap && !status.edgeMap.suppressed && status.edgeMap.total > 0 ? (
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>
            Edge map
            <span className={styles.cardSubtitle}>
              {status.edgeMap.total} round-trips · last {status.edgeMap.lookback_days} days · what Sonnet sees in its
              prompt
            </span>
          </h3>
          <div className={styles.edgeMapGrid}>
            {Object.entries(status.edgeMap.by_side_phase).length > 0 ? (
              <div className={styles.edgeMapGroup}>
                <div className={styles.edgeMapGroupLabel}>Side × session phase</div>
                <div className={styles.edgeMapBuckets}>
                  {Object.entries(status.edgeMap.by_side_phase)
                    .sort((a, b) => b[1].count - a[1].count)
                    .map(([key, b]) => (
                      <div
                        key={`sp-${key}`}
                        className={`${styles.edgeBucket} ${b.avg_pnl >= 0 ? styles.edgeBucketWin : styles.edgeBucketLose}`}
                      >
                        <div className={styles.edgeBucketKey}>{key}</div>
                        <div className={styles.edgeBucketStats}>
                          <span>{Math.round(b.win_rate * 100)}% WR</span>
                          <span>
                            {b.avg_pnl >= 0 ? '+' : ''}
                            {b.avg_pnl.toFixed(0)} avg
                          </span>
                        </div>
                        <div className={styles.edgeBucketCount}>{b.count} trades</div>
                      </div>
                    ))}
                </div>
              </div>
            ) : null}
            {Object.entries(status.edgeMap.by_side_trend).length > 0 ? (
              <div className={styles.edgeMapGroup}>
                <div className={styles.edgeMapGroupLabel}>Side × daily trend</div>
                <div className={styles.edgeMapBuckets}>
                  {Object.entries(status.edgeMap.by_side_trend)
                    .sort((a, b) => b[1].count - a[1].count)
                    .map(([key, b]) => (
                      <div
                        key={`st-${key}`}
                        className={`${styles.edgeBucket} ${b.avg_pnl >= 0 ? styles.edgeBucketWin : styles.edgeBucketLose}`}
                      >
                        <div className={styles.edgeBucketKey}>{key}</div>
                        <div className={styles.edgeBucketStats}>
                          <span>{Math.round(b.win_rate * 100)}% WR</span>
                          <span>
                            {b.avg_pnl >= 0 ? '+' : ''}
                            {b.avg_pnl.toFixed(0)} avg
                          </span>
                        </div>
                        <div className={styles.edgeBucketCount}>{b.count} trades</div>
                      </div>
                    ))}
                </div>
              </div>
            ) : null}
            {Object.entries(status.edgeMap.by_side).length > 0 ? (
              <div className={styles.edgeMapGroup}>
                <div className={styles.edgeMapGroupLabel}>By side</div>
                <div className={styles.edgeMapBuckets}>
                  {Object.entries(status.edgeMap.by_side)
                    .sort((a, b) => b[1].count - a[1].count)
                    .map(([key, b]) => (
                      <div
                        key={`s-${key}`}
                        className={`${styles.edgeBucket} ${b.avg_pnl >= 0 ? styles.edgeBucketWin : styles.edgeBucketLose}`}
                      >
                        <div className={styles.edgeBucketKey}>{key}</div>
                        <div className={styles.edgeBucketStats}>
                          <span>{Math.round(b.win_rate * 100)}% WR</span>
                          <span>
                            {b.avg_pnl >= 0 ? '+' : ''}
                            {b.avg_pnl.toFixed(0)} avg
                          </span>
                        </div>
                        <div className={styles.edgeBucketCount}>{b.count} trades</div>
                      </div>
                    ))}
                </div>
              </div>
            ) : null}
          </div>
        </section>
      ) : status?.edgeMap?.suppressed ? (
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Edge map</h3>
          <div className={styles.empty}>
            Need {15 - (status.edgeMap.total ?? 0)} more closed round-trips before bucket stats are statistically
            meaningful. Currently {status.edgeMap.total ?? 0} closed.
          </div>
        </section>
      ) : null}

      {/* ── Current position + last decision ── */}
      <div className={styles.twoCol}>
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Current position</h3>
          {equityPos ? (
            <div className={styles.posBody}>
              <div className={styles.posHeader}>
                <span className={equityPos.side === 'long' ? styles.sideLong : styles.sideShort}>
                  {equityPos.side.toUpperCase()} {equityPos.qty} {equityPos.symbol}
                </span>
                <span className={styles.posHint}>since {timeAgo(equityPos.entry_time)}</span>
              </div>
              <div className={styles.posMetrics}>
                <div>
                  <span className={styles.metricLabel}>Entry</span>
                  <span className={styles.metricValue}>${equityPos.entry_price.toFixed(3)}</span>
                </div>
                <div>
                  <span className={styles.metricLabel}>Initial stop</span>
                  <span className={styles.metricValue}>${equityPos.initial_stop.toFixed(3)}</span>
                </div>
                <div>
                  <span className={styles.metricLabel}>Current stop</span>
                  <span className={styles.metricValue}>${equityPos.current_stop.toFixed(3)}</span>
                </div>
                <div>
                  <span className={styles.metricLabel}>First target</span>
                  <span className={styles.metricValue}>${equityPos.initial_target.toFixed(3)}</span>
                </div>
                <div>
                  <span className={styles.metricLabel}>Session extreme</span>
                  <span className={styles.metricValue}>${equityPos.session_extreme.toFixed(3)}</span>
                </div>
                <div>
                  <span className={styles.metricLabel}>Scaled out?</span>
                  <span className={styles.metricValue}>{equityPos.scaled_out ? 'Yes (50%)' : 'No'}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.empty}>Flat — waiting for the next setup.</div>
          )}
        </section>

        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Last decision</h3>
          {lastDec ? (
            <div className={styles.lastDecision}>
              <div className={styles.lastDecisionHeader}>
                <span className={`${styles.actionBadge} ${actionClass(lastDec.action)}`}>{lastDec.action}</span>
                <span className={styles.confidence}>Confidence {lastDec.confidence}</span>
                {status?.circuitBreaker?.armed ? (
                  <span
                    className={styles.cbBadge}
                    title="Consecutive-loss circuit breaker is currently raising the entry confidence floor"
                  >
                    CB armed · {status.circuitBreaker.loss_count}L · {cbMinutesLeft(status.circuitBreaker.expires_at)}
                  </span>
                ) : null}
              </div>
              <p className={styles.reasoning}>{lastDec.reasoning}</p>
              <div className={styles.lastDecisionFoot}>Updated {timeAgo(status?.updatedAt)}</div>
            </div>
          ) : (
            <div className={styles.empty}>No decisions yet.</div>
          )}
        </section>
      </div>

      {/* ── Recent tick timeline ── */}
      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Recent ticks</h3>
        {status && status.recentTicks.length > 0 ? (
          <div className={styles.ticksWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Price</th>
                  <th>Action</th>
                  <th>Conf.</th>
                  <th>Reasoning</th>
                </tr>
              </thead>
              <tbody>
                {status.recentTicks.map((tick) => (
                  <tr key={tick.SK}>
                    <td>{formatTimeLocal(tick.createdAt)}</td>
                    <td className={styles.num}>${tick.price.toFixed(3)}</td>
                    <td>
                      <span className={`${styles.actionBadge} ${actionClass(tick.action)}`}>{tick.action}</span>
                    </td>
                    <td className={styles.num}>{tick.confidence}</td>
                    <td className={styles.reasonCell}>{tick.reasoning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.empty}>No ticks yet — once enabled, decisions land here every 2 minutes.</div>
        )}
      </section>

      {/* ── Trade log ── */}
      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Closed trades</h3>
        {trades.length > 0 ? (
          <div className={styles.ticksWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>Side</th>
                  <th>Qty</th>
                  <th>Entry $</th>
                  <th>Exit $</th>
                  <th>P&amp;L $</th>
                  <th>P&amp;L %</th>
                  <th>Hold</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t) => (
                  <tr key={t.SK}>
                    <td>{formatTimeLocal(t.entryTime)}</td>
                    <td>{formatTimeLocal(t.exitTime)}</td>
                    <td>{t.side}</td>
                    <td className={styles.num}>{t.qty}</td>
                    <td className={styles.num}>${t.entryPrice.toFixed(3)}</td>
                    <td className={styles.num}>${t.exitPrice.toFixed(3)}</td>
                    <td className={`${styles.num} ${pnlClass(t.pnlDollars)}`}>
                      {t.pnlDollars >= 0 ? '+' : ''}${t.pnlDollars.toFixed(2)}
                    </td>
                    <td className={`${styles.num} ${pnlClass(t.pnlDollars)}`}>
                      {t.pnlPct >= 0 ? '+' : ''}
                      {t.pnlPct.toFixed(2)}%
                    </td>
                    <td className={styles.num}>{t.holdMinutes.toFixed(1)}m</td>
                    <td>
                      {t.partial ? (
                        <span className={styles.partialBadge} title="50% locked at first target — runner still managed">
                          Scale-out
                        </span>
                      ) : (
                        t.exitReason
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.empty}>No closed trades yet.</div>
        )}
      </section>

      {/* ── Credentials management (once connected) ── */}
      {config?.credentialsConfigured && (
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Scalper account</h3>
          <p className={styles.cardBody}>
            Alpaca paper credentials are configured. Rotate or remove them any time — removing credentials will pause
            the scalper automatically.
          </p>
          <button type="button" className={styles.btnDanger} onClick={handleDeleteCredentials}>
            Remove credentials
          </button>
        </section>
      )}
    </div>
  );
};

export default Scalper;
